import logger from '../utils/logger';
/**
 * Utility functions for date operations
 */

// Cache for actuarial tables data
let actuarialTablesCache = null;

/**
 * Load actuarial tables with caching
 * @returns {Promise<Object>} Actuarial tables data
 */
const loadActuarialTables = async () => {
  if (actuarialTablesCache) {
    return actuarialTablesCache;
  }

  try {
    const response = await fetch(
      `${window.location.origin}/data/irs_actuarial_tables.json`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to load actuarial tables: ${response.status} ${response.statusText}`,
      );
    }

    actuarialTablesCache = await response.json();
    return actuarialTablesCache;
  } catch (error) {
    logger.error("Error loading actuarial tables:", error);
    throw error;
  }
};

/**
 * Calculate age based on date of birth
 * @param {string|Date} dateOfBirth - Date of birth as string (YYYY-MM-DD) or Date object
 * @returns {number} Age in years (including fractional years, rounded to 2 decimals)
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return 0;

  try {
    const age = getYearDifference(dateOfBirth);
    return Math.round(age * 100) / 100; // Round to 2 decimal places
  } catch (error) {
    logger.error("Error calculating age:", error);
    return 0;
  }
};

/**
 * Format a date as YYYY-MM-DD
 * @param {Date} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return "";

  const d = date instanceof Date ? date : new Date(date);
  return d.toISOString().split("T")[0];
};

/**
 * Get current date in YYYY-MM-DD format
 * @returns {string} Today's date as string
 */
export const getCurrentDate = () => {
  return formatDate(new Date());
};

/**
 * Calculate the difference between two dates in years
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date (defaults to today)
 * @returns {number} Difference in years (can be decimal)
 */
export const getYearDifference = (startDate, endDate = new Date()) => {
  if (!startDate) return 0;

  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  // Calculate difference in milliseconds and convert to years
  const diffMs = end - start;
  const msPerYear = 1000 * 60 * 60 * 24 * 365.25; // Account for leap years

  return diffMs / msPerYear;
};

/**
 * Formats a date string to a more readable format
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date string (e.g., "January 1, 2024")
 */
export const formatDateReadable = (dateString) => {
  if (!dateString) return "";

  const options = { year: "numeric", month: "long", day: "numeric" };
  return new Date(dateString).toLocaleDateString("en-US", options);
};

/**
 * Converts a date string to ISO format
 * @param {string} dateString - Date string in any format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const toISODate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
};

/**
 * Calculate exact life expectancy by adding current age to IRS table value
 * @param {number} age - Current age in years (including fractional years)
 * @param {string} gender - 'male' or 'female'
 * @returns {Promise<number>} Total life expectancy rounded to 2 decimals
 */
export const getExactLifeExpectancy = async (age, gender) => {
  if (!age || !gender) return 0;

  try {
    // Get the base life expectancy from IRS table
    const baseLifeExpectancy = await getIRSLifeExpectancy(
      Math.floor(age),
      gender,
    );

    // Calculate total life expectancy by adding exact age
    const totalLifeExpectancy = age + baseLifeExpectancy;

    // Round to 2 decimal places
    return Math.round(totalLifeExpectancy * 100) / 100;
  } catch (error) {
    logger.error("Error calculating exact life expectancy:", error);
    return 0;
  }
};

/**
 * Get life expectancy from IRS actuarial tables
 * @param {number} age - Current age in years
 * @param {string} gender - 'male' or 'female'
 * @returns {number} Life expectancy from IRS tables
 */
export const getIRSLifeExpectancy = async (age, gender) => {
  if (!age || !gender || age < 0 || age > 119) {
    logger.error("Invalid age or gender:", { age, gender });
    return 0;
  }

  try {
    const actuarialTables = await loadActuarialTables();
    const genderTable = actuarialTables.life_table[gender.toLowerCase()];

    if (!genderTable) {
      throw new Error(`Invalid gender: ${gender}`);
    }

    const ageEntry = genderTable.find((entry) => entry.age === Math.floor(age));
    if (ageEntry) {
      return ageEntry.life_expectancy;
    }

    // If exact age not found, interpolate between closest ages
    const sortedAges = genderTable
      .map((entry) => entry.age)
      .sort((a, b) => a - b);
    const lowerAge = sortedAges.filter((a) => a <= age).pop();
    const upperAge = sortedAges.filter((a) => a > age).shift();

    if (lowerAge !== undefined && upperAge !== undefined) {
      const lowerEntry = genderTable.find((entry) => entry.age === lowerAge);
      const upperEntry = genderTable.find((entry) => entry.age === upperAge);

      // Linear interpolation
      const ageDiff = upperAge - lowerAge;
      const expectancyDiff =
        upperEntry.life_expectancy - lowerEntry.life_expectancy;
      const ratio = (age - lowerAge) / ageDiff;

      return lowerEntry.life_expectancy + expectancyDiff * ratio;
    }

    throw new Error(`No life expectancy data found for age ${age}`);
  } catch (error) {
    logger.error("Error calculating life expectancy:", error);

    // Provide fallback values based on general life expectancy patterns
    // These are rough estimates and should only be used when the actual data is unavailable
    if (gender.toLowerCase() === "female") {
      return Math.max(0, 84 - age); // Average female life expectancy ~84
    } else {
      return Math.max(0, 79 - age); // Average male life expectancy ~79
    }
  }
};
